import type { IEventChannel } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const $: JQueryStatic = global.$;
const es = global.es;

/**
 * @class StyleModule
 */
export default class StyleModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        this.getDefaultBasicText = this.getDefaultBasicText.bind(this);
        this.generateView = this.generateView.bind(this);
        this.getImageUrl = this.getImageUrl.bind(this);
        this.appendView = this.appendView.bind(this);
        this.getTextFromStyle = this.getTextFromStyle.bind(this);
        this.setText = this.setText.bind(this);
        this.setLabelMode = this.setLabelMode.bind(this);
        this.getMainColor = this.getMainColor.bind(this);
        this.withColor = this.withColor.bind(this);
        this.withSymbol = this.withSymbol.bind(this);
        this.containsPoint = this.containsPoint.bind(this);
        this.getFocalPoint = this.getFocalPoint.bind(this);
        this.getFocalBounds = this.getFocalBounds.bind(this);
    }

    /**
     * 
     * @returns 
     */
    getDefaultBasicText() {
        // see serverside equivalent of this in BasicText.java DEFAULT_BASIC_TEXT - try to keep these in sync (or create a rest endpoint to fetch it from server)
        const DEFAULT_BASIC_TEXT = {
            "color": es.util.color.fromHex("#2E3C8C"), // #000 #2e2e2e
            "fontOutlineColor": es.util.color.fromHex("#FFF"),
            "family": "Arimo",
            "height": 12,
            "bold": false,
            "italic": false,
            "position": "BELOW",
        };
        
        return DEFAULT_BASIC_TEXT;
    }

    /**
     * 
     * @param style 
     * @param width 
     * @param height
     *  
     * @returns 
     */
    generateView(style: object, width: number, height: number) {
        const imgSrc = this.getImageUrl(style, width, height);
        const dfd = $.Deferred();
        const img = new Image();
        img.onload = () => {
            dfd.resolve(img);
        };
        img.onerror = (obj) => {
            es.log.error("generateView", obj, style, width, height);
            dfd.reject.apply(dfd, arguments);
        };
        img.src = imgSrc;
        return dfd.promise();
    }

    /**
     * 
     * @param style 
     * @param width 
     * @param height
     *  
     * @returns 
     */
    getImageUrl(style: object, width: number, height: number) {
        if (!style) {
            throw new Error("Missing style!");
        }
        
        const data = {
            style : style,
            width : width ? width : 16,
            height : height ? height : 16
        };

        if (es.isPublicAccess) {
            data.TENANT_ID = es.tenantId;
        }

        return es.util.URLON.toUrl(data, es.api.getURL("style")+"?");
    }
    
    /**
     * 
     * @param style 
     * @param element 
     * @param width 
     * @param height 
     * 
     * @returns 
     */
    appendView(style: object, element: HTMLElement, width: number, height: number) {
        return this.generateView(style, width, height).done((img) => {
            $(element).append(img);
        });
    }

    /**
     * Get the root basicText element from a style.
     * e.g. StyleAndPolygon.symbolStyle.label.text is returned
     */
    getTextFromStyle(style: object) {
        return es.util.style.types[style.$type].getText(style);
    }

    /**
     * Convenience method to set the text on the style, wherever it may be (looking at you, SymbolAndPolygonStyle).
     * 
     * @param style
     * @param newText
     */
    setText(style: object, newText: string) {
        if (style.$type === "SymbolAndPolygonStyle") {
            style.symbolStyle.text = newText;
        } else {
            style.text = newText
        }
    }

    /**
     * Set LabelMode (i.e. "All LABELS, NONE") on the style
     * The label will be added depending on the old mode and some style properties 
     * 
     * @param style 
     * @param newMode 
     * @param isNewLabel 
     */
    setLabelMode(style: object, newMode: string, isNewLabel: boolean) {
        if (newMode !== "NONE") {
            const generateNewLabel = isNewLabel; // if the previous mode was none we have to generate a new label
            if (style.$type === "SymbolStyle") {
                style.text = generateNewLabel ? this.getDefaultBasicText() : style.text;
            } else if (style.$type === "SymbolAndPolygonStyle") {
                style.symbolStyle.text = generateNewLabel ? this.getDefaultBasicText() : style.symbolStyle.text;
            } else {
                // polygon
                if (style && style.text){
                    // skip
                } else {
                    const basicText = this.getDefaultBasicText();
                    basicText.position = "CENTRAL"; // force central positioning on boxed polygon labels,
                    style.text = basicText;
                }
            }
        }
    }
    
    /**
     * 
     * @param style 
     * 
     * @returns 
     */
    getMainColor(style: object) {
        return es.util.style.types[style.$type].mainColor(style);
    }
    
    /**
     * 
     * @param style 
     * @param color 
     * 
     * @returns 
     */
    withColor(style: object, color) {
        return es.util.style.types[style.$type].withColor(style, color);
    }
    
    /**
     * 
     * @param style 
     * @param symbolTypeId 
     * 
     * @returns 
     */
    withSymbol(style: object, symbolTypeId: number) {
        return es.util.style.types[style.$type].withSymbol(style, symbolTypeId);
    }
    
    /**
     * Determine if the geometry given contains the point given when using the style given.
     * 
     * @param style 
     * @param geom 
     * @param point 
     * @param resolution 
     * 
     * @returns 
     */
    containsPoint(style: object, geom, point, resolution: number) {
        return es.util.style.types[style.$type].containsPoint(style, geom, point, resolution);
    }
    
    /**
     * Get the focal point given the style and geometry given
     * 
     * @param style 
     * @param geom 
     * @param pnt 
     * @returns 
     */
    getFocalPoint(style: object, geom, pnt) {
        return es.util.style.types[style.$type].getFocalPoint(style, geom, pnt);
    }
    
    /**
     * 
     * @param style 
     * @param geom 
     * 
     * @returns 
     */
    getFocalBounds(style: object, geom) {
        return es.util.style.types[style.$type].getFocalBounds(style, geom);
    }
}
